import { useEffect, useRef } from 'react';

// Custom hook for swipe back gesture on mobile devices
export const useSwipeBack = (onSwipeBack, isEnabled = true) => {
  const swipeBackElementRef = useRef(null);
  const touchDataRef = useRef({
    startX: 0,
    startY: 0,
    endX: 0,
    endY: 0,
    type: null,
    notified: false,
    feedbackDelay: false,
    isCleaningUp: false, // Flag to prevent element creation during cleanup
    touchTimeout: null, // Timeout to reset stuck touch states
    lastTouchTime: 0 // Track when the last touch event occurred
  });

  useEffect(() => {
    if (!onSwipeBack || !isEnabled) return;

    const threshold = 120;

    // Create swipe back visual indicator element
    const createSwipeBackElement = () => {
      if (swipeBackElementRef.current || touchDataRef.current.isCleaningUp) return;
      
      const element = document.createElement('div');
      element.className = 'tm-swipe-back';
      document.body.appendChild(element);
      swipeBackElementRef.current = element;
    };

    // Remove swipe back element
    const removeSwipeBackElement = () => {
      touchDataRef.current.isCleaningUp = true;
      
      // Force immediate cleanup - remove by class name as well
      const existingElements = document.querySelectorAll('.tm-swipe-back');
      existingElements.forEach(el => {
        try {
          if (el.parentNode) {
            el.parentNode.removeChild(el);
          }
        } catch (e) {
          // Element might have already been removed
        }
      });
      
      if (swipeBackElementRef.current) {
        try {
          if (swipeBackElementRef.current.parentNode) {
            document.body.removeChild(swipeBackElementRef.current);
          }
        } catch (e) {
          // Element might have already been removed
        }
        swipeBackElementRef.current = null;
      }
      
      // Reset cleanup flag after a short delay
      setTimeout(() => {
        touchDataRef.current.isCleaningUp = false;
      }, 50);
    };

    // Asymptotic interpolation function
    const asymptoticInterp = (t, start, end) => {
      if (t <= 0) return start;
      return start + (end - start) * (t / (t + 0.5));
    };

    const handleTouchStart = (event) => {
      // Clean up any existing elements from previous gestures
      if (swipeBackElementRef.current) {
        removeSwipeBackElement();
      }
      
      // Clear any existing timeout
      if (touchDataRef.current.touchTimeout) {
        clearTimeout(touchDataRef.current.touchTimeout);
      }
      
      // Force reset touch state on new touch start
      touchDataRef.current.type = null;
      touchDataRef.current.notified = false;
      touchDataRef.current.lastTouchTime = Date.now();
      
      const touch = event.touches[0];
      touchDataRef.current.startX = touch.screenX;
      touchDataRef.current.startY = touch.screenY;
      
      // Set a failsafe timeout to reset touch state if touchend/touchcancel don't fire
      touchDataRef.current.touchTimeout = setTimeout(() => {
        if (touchDataRef.current.type) {
          removeSwipeBackElement();
          touchDataRef.current.type = null;
          touchDataRef.current.notified = false;
        }
      }, 3000); // 3 second timeout
    };

    const handleTouchMove = (event) => {
      touchDataRef.current.lastTouchTime = Date.now(); // Update last touch time
      
      const touch = event.changedTouches[0];
      touchDataRef.current.endX = touch.screenX;
      touchDataRef.current.endY = touch.screenY;

      const deltaX = touchDataRef.current.endX - touchDataRef.current.startX;
      const deltaY = touchDataRef.current.endY - touchDataRef.current.startY;

      const isHorizontal = Math.abs(deltaX) > 30 && Math.abs(deltaY) < 30;

      // Determine if this is a horizontal swipe gesture
      if (touchDataRef.current.type === 'h') {
        event.preventDefault();
      } else if (!event.cancelable) {
        return;
      }

      if (isHorizontal && !touchDataRef.current.type && event.cancelable) {
        touchDataRef.current.type = 'h';
        event.preventDefault();
        
        // Only create swipe back element for rightward swipes (positive deltaX)
        if (deltaX > 0) {
          createSwipeBackElement(); // Create element when horizontal swipe is detected
        }
      }

      // Only process if we have a horizontal swipe, the element exists, and we're swiping right
      if (touchDataRef.current.type === 'h' && swipeBackElementRef.current && deltaX > 0) {
        // Haptic feedback when threshold is crossed
        if (deltaX > threshold && !touchDataRef.current.notified) {
          touchDataRef.current.notified = true;
          touchDataRef.current.feedbackDelay = true;
          setTimeout(() => {
            touchDataRef.current.feedbackDelay = false;
          }, 80);
          
          // Telegram WebApp haptic feedback
          if (window.Telegram?.WebApp?.HapticFeedback) {
            window.Telegram.WebApp.HapticFeedback.impactOccurred('soft');
          }
        }

        // Calculate swipe back element position
        let translateX = asymptoticInterp(deltaX / 80, 0, 100);
        translateX = Math.max(0, translateX - 20); // Ensure we don't go negative
        translateX = Math.min(translateX, 50); // Prevent sliding too far out (max 50px from edge)
        swipeBackElementRef.current.style.left = `${translateX - 52}px`;
      }
    };

    const handleTouchEnd = (event) => {
      // Clear the failsafe timeout
      if (touchDataRef.current.touchTimeout) {
        clearTimeout(touchDataRef.current.touchTimeout);
        touchDataRef.current.touchTimeout = null;
      }
      
      // Update final coordinates from touchend event if available
      if (event.changedTouches && event.changedTouches.length > 0) {
        const touch = event.changedTouches[0];
        touchDataRef.current.endX = touch.screenX;
        touchDataRef.current.endY = touch.screenY;
      }
      
      const deltaX = touchDataRef.current.endX - touchDataRef.current.startX;

      // Check if swipe back should trigger
      if (touchDataRef.current.type === 'h' && deltaX > threshold) {
        if (!touchDataRef.current.feedbackDelay && window.Telegram?.WebApp?.HapticFeedback) {
          window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
        }
        onSwipeBack();
      }

      // Immediately remove elements without animation
      removeSwipeBackElement();

      // Reset state
      touchDataRef.current.notified = false;
      touchDataRef.current.type = null;
    };

    // Handle touch cancel (when swipe is interrupted)
    const handleTouchCancel = () => {
      // Clear the failsafe timeout
      if (touchDataRef.current.touchTimeout) {
        clearTimeout(touchDataRef.current.touchTimeout);
        touchDataRef.current.touchTimeout = null;
      }
      
      // Immediately remove elements without animation
      removeSwipeBackElement();
      
      // Reset state
      touchDataRef.current.notified = false;
      touchDataRef.current.type = null;
    };

    // Add event listeners
    // Note: touchstart must be non-passive on Android for touchmove.preventDefault() to work
    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('touchcancel', handleTouchCancel);

    // Add additional cleanup event listeners
    const handleWindowBlur = () => {
      removeSwipeBackElement();
    };
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        removeSwipeBackElement();
      }
    };
    
    // Add scroll listener to clean up when page scrolls
    const handleScroll = () => {
      if (swipeBackElementRef.current) {
        removeSwipeBackElement();
      }
    };
    
    window.addEventListener('blur', handleWindowBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Add a periodic cleanup check
    const cleanupInterval = setInterval(() => {
      const existingElements = document.querySelectorAll('.tm-swipe-back');
      const timeSinceLastTouch = Date.now() - touchDataRef.current.lastTouchTime;
      
      // Only clean up orphaned elements if no recent touch activity (more than 1 second ago)
      if (existingElements.length > 0 && timeSinceLastTouch > 1000) {
        removeSwipeBackElement();
        // Reset touch state as well since elements shouldn't exist without active touch
        touchDataRef.current.type = null;
        touchDataRef.current.notified = false;
      }
    }, 500);

    // Cleanup function
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchCancel);
      window.removeEventListener('blur', handleWindowBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('scroll', handleScroll);
      clearInterval(cleanupInterval);
      
      // Clear any pending timeout
      if (touchDataRef.current.touchTimeout) {
        clearTimeout(touchDataRef.current.touchTimeout);
      }
      
      // Force cleanup of any remaining elements
      try {
        removeSwipeBackElement();
      } catch (e) {
        // Ignore errors during cleanup
      }
    };
  }, [onSwipeBack, isEnabled]);
};